/*
 * Traducerea textelor aplicatiei.
 *
 * ---------------------------------------------------------------------------
 * CHEIA E CHIAR TEXTUL ENGLEZESC
 * ---------------------------------------------------------------------------
 *
 * `t('Add to cart')`, nu `t('cart.add')`. Doua motive, si al doilea conteaza
 * mai mult decat pare:
 *
 *   1. Nu trebuie inventata si tinuta minte o schema de chei pentru vreo suta
 *      de propozitii - si nici cautata cheia cand vrei sa stii ce scrie pe
 *      buton.
 *   2. O traducere care lipseste nu lasa ecranul cu `cart.add` scris pe el.
 *      Lipsa se vede ca text englezesc intr-o pagina romaneasca: neplacut, dar
 *      citibil, si nimeni nu ramane blocat din cauza ei.
 *
 * Pretul e ca schimbarea unui text englezesc rupe traducerea lui in tacere.
 * Asumat: alternativa - chei simbolice - rupe ecranul, nu traducerea.
 *
 * ---------------------------------------------------------------------------
 * CE NU SE TRADUCE
 * ---------------------------------------------------------------------------
 *
 * Nimic din ce vine din baza de date: numele serviciilor, categoriile,
 * descrierile, numele statusurilor din flux. Alea sunt configurate de banca si
 * traduse tot acolo, daca vrea; o lista de traduceri scrisa aici ar ramane in
 * urma primei configurari noi si ar minti cu incredere.
 *
 * ---------------------------------------------------------------------------
 * DE CE NU E CONTEXT REACT
 * ---------------------------------------------------------------------------
 *
 * `t` e o functie obisnuita, importabila oriunde, iar limba sta intr-o
 * variabila de modul. Un context ar fi cerut un `useT()` in fiecare componenta
 * care scrie un cuvant - inclusiv in cele fara nicio alta stare - si ar fi
 * facut din traducere o schimbare de structura, nu una de text.
 *
 * Reactia la schimbarea limbii se face remontand arborele, cu `key={lang}` in
 * App. E o solutie de ciocan, dar limba se schimba de doua ori pe an: costul e
 * o remontare la un clic pe care nimeni nu-l apasa des.
 */

export type Lang = 'en' | 'ro';

const STORAGE_KEY = 'catalog.lang';

/**
 * Traducerile. Cheia e textul englezesc, exact cum apare in cod.
 *
 * Diacritice complete: "cererea" fara ele arata a text scris in graba, iar
 * ecranul asta ajunge in fata unei banci.
 */
const RO: Record<string, string> = {
  /* Cadru si navigatie */
  'IT Service Catalog': 'Catalog de Servicii IT',
  /* Marca, pe doua randuri: bara arata unde se taie. Vezi AppShell. */
  'IT Service|Catalog': 'Catalog de|Servicii IT',
  Cart: 'Coș',
  /* Numai pentru cititoarele de ecran, pe coloana chevronului. */
  Open: 'Deschide',
  'My requests': 'Cererile mele',
  'Skip to content': 'Sari la conținut',
  'Open menu': 'Deschide meniul',
  'Close menu': 'Închide meniul',
  'Signed in as': 'Autentificat ca',
  'Unknown user': 'Utilizator necunoscut',
  'Sign out': 'Deconectare',
  'Signing out…': 'Se deconectează…',
  Language: 'Limbă',

  /* Autentificare */
  'Sign in': 'Autentificare',
  'Signing in…': 'Se autentifică…',
  'Sign-in providers could not be loaded': 'Furnizorii de autentificare nu au putut fi încărcați',
  'You can still sign in with your username and password.':
    'Te poți autentifica în continuare cu numele de utilizator și parola.',
  'Continue with': 'Continuă cu',
  'or continue with': 'sau continuă cu',
  /* Etichetele furnizorilor de autentificare. Vin din api, dar cheia e textul
     englezesc, deci se traduc ca oricare altul. Numele protocoalelor raman -
     OpenID Connect si SAML sunt nume proprii, nu cuvinte. */
  'Sign in with your domain account': 'Autentificare cu contul de domeniu',
  'Sign in with OpenID Connect': 'Autentificare cu OpenID Connect',
  'Sign in with SAML': 'Autentificare cu SAML',

  /* Catalog */
  'Search services': 'Caută servicii',
  Search: 'Caută',
  'All categories': 'Toate categoriile',
  'No services found': 'Niciun serviciu găsit',
  'Nothing matches what you typed. Try fewer words.':
    'Nimic nu se potrivește cu ce ai scris. Încearcă mai puține cuvinte.',
  'Add to cart': 'Adaugă în coș',
  'Adding…': 'Se adaugă…',
  'View details': 'Vezi detalii',
  'Back to catalog': 'Înapoi la catalog',
  'What happens next': 'Ce urmează',
  'This workflow has no stages configured yet.':
    'Acest flux nu are încă etape configurate.',
  'The ticket type of this service has no workflow yet, so the stages cannot be shown. The service can still be requested.':
    'Tipul de tichet al acestui serviciu nu are încă un flux, deci etapele nu pot fi afișate. Serviciul poate fi cerut oricum.',
  Cost: 'Cost',
  'No charge': 'Fără cost',
  /*
   * "Cost serviciu", nu "Cost", si "Tinta SLA", nu "Termen".
   *
   * Cele doua cifre de pe cardul de catalog erau botezate ca si cum ar fi
   * pretul comenzii si data livrarii. Niciuna nu e:
   *
   *   `pricing` e cat costa SERVICIUL, de obicei pe luna - langa un buton
   *   "Adauga in cos" se citea drept cat costa ce pui in cos. Acelasi cuvant
   *   "Cost" mai apare de doua ori in aplicatie, in cos si pe cerere, si acolo
   *   chiar inseamna cat costa linia aia: acelasi nume pentru doua marimi
   *   diferite, la doua clicuri distanta.
   *
   *   `sla` e timpul de RASPUNS din contract, nu termenul de livrare.
   *   "Termen" spune "pana atunci il primesti", ceea ce e altceva. "Card
   *   transaction authorisation - 15 minutes" nu inseamna ca primesti ceva in
   *   cincisprezece minute, ci ca se raspunde in cincisprezece minute daca
   *   serviciul are o problema.
   *
   * "De raspuns", nu doar "SLA": sablonul comercial are TREI campuri de SLA -
   * raspuns, interventie, rezolvare - iar api-ul il trimite pe primul
   * (`t.sla_id` -> `it_customers_sla_types`, pe care portalul il numeste peste
   * tot "Response SLA"). Un card care ar scrie doar "SLA" ar lasa cititorul sa
   * aleaga singur care dintre cele trei, si cei mai multi ar alege rezolvarea.
   */
  'Service cost': 'Cost serviciu',
  'Response SLA': 'SLA de răspuns',
  'Configuration item': 'Element de configurare',
  Quantity: 'Cantitate',
  'Additional note': 'Notă suplimentară',
  optional: 'opțional',
  'Needs approval': 'Necesită aprobare',
  'Goes straight to IT': 'Merge direct la IT',

  /* Coș */
  'Your cart': 'Coșul tău',
  'Your cart is empty': 'Coșul tău este gol',
  'Pick a service from the catalog and it will show up here.':
    'Alege un serviciu din catalog și va apărea aici.',
  Estimated: 'Estimat',
  'Who is it for': 'Pentru cine este',
  Me: 'Pentru mine',
  'The ticket is opened for this person, not for you.':
    'Tichetul se deschide pentru această persoană, nu pentru tine.',
  Justification: 'Justificare',
  'Why you need this.': 'De ce ai nevoie de acest lucru.',
  'Goes on the ticket, for whoever decides.':
    'Ajunge în descrierea tichetului, pentru cine decide.',
  'Submit request': 'Trimite cererea',
  'Submitting…': 'Se trimite…',
  'Keep looking': 'Caută mai departe',
  Remove: 'Șterge',
  Removing: 'Se șterge',

  /* Cereri */
  Request: 'Cerere',
  Requests: 'Cereri',
  'No requests yet': 'Nicio cerere încă',
  'Requests you send from the catalog show up here.':
    'Cererile trimise din catalog apar aici.',
  'Back to my requests': 'Înapoi la cererile mele',
  Submitted: 'Trimisă',
  Items: 'Articole',
  Item: 'Articol',
  Service: 'Serviciu',
  Note: 'Notă',
  Qty: 'Cant.',
  Ticket: 'Tichet',
  'This request has no items.': 'Această cerere nu are articole.',
  History: 'Istoric',
  'No history entries yet.': 'Niciun eveniment încă.',
  System: 'Sistem',

  /* Stari si mesaje generale */
  Loading: 'Se încarcă',
  'Loading…': 'Se încarcă…',
  'Loading request…': 'Se încarcă cererea…',
  'Something went wrong': 'Ceva nu a mers',
  'Try again': 'Încearcă din nou',
  Retry: 'Reîncearcă',
  'This screen could not be displayed': 'Acest ecran nu a putut fi afișat',
  'Something in the page failed while drawing it. The request itself may have gone through — check the list before sending it again.':
    'Ceva din pagină a eșuat la desenare. Cererea poate să fi ajuns totuși — verifică lista înainte să o trimiți din nou.',
  Reload: 'Reîncarcă',
  Previous: 'Înapoi',
  Next: 'Înainte',
  Page: 'Pagina',
  of: 'din',

  /* Cos */
  Actions: 'Acțiuni',
  'Ready to submit': 'Gata de trimis',
  'Review what you are about to request, then submit it.':
    'Verifică ce urmează să ceri, apoi trimite.',
  'Add services from the catalog and they will appear here.':
    'Adaugă servicii din catalog și vor apărea aici.',
  'Browse the catalog': 'Vezi catalogul',
  'Loading your cart…': 'Se încarcă coșul…',
  'Removing…': 'Se șterge…',
  Total: 'Total',
  'Write why you need this.': 'Scrie de ce ai nevoie.',
  'Item removed.': 'Articol șters.',
  'Could not remove the item.': 'Articolul nu a putut fi șters.',
  'Request submitted.': 'Cerere trimisă.',
  'The request could not be submitted.': 'Cererea nu a putut fi trimisă.',

  /* Fisa serviciului */
  'About this service': 'Despre acest serviciu',
  'What makes up the cost': 'Din ce se compune costul',
  'Anything the team should know about this one.':
    'Orice ar trebui să știe echipa despre acesta.',
  'Loading service…': 'Se încarcă serviciul…',
  stage: 'etapă',
  stages: 'etape',

  /* Lista de catalog */
  'Service catalog': 'Catalog de servicii',
  'Choose a service, review what it involves, then add it to your cart.':
    'Alege un serviciu, vezi ce presupune, apoi adaugă-l în coș.',
  'Service name': 'Numele serviciului',
  Category: 'Categorie',
  Clear: 'Golește',
  Details: 'Detalii',
  'Loading services…': 'Se încarcă serviciile…',
  'No services are published for your company yet.':
    'Niciun serviciu nu este publicat pentru compania ta încă.',
  'No services match': 'Niciun serviciu găsit',
  'Try a different search term, or clear the filters.':
    'Încearcă alt cuvânt de căutare sau golește filtrele.',

  /* Autentificare */
  Username: 'Nume de utilizator',
  Password: 'Parolă',
  'Please fill in both your username and your password.':
    'Completează și numele de utilizator, și parola.',
  'Checking single sign-on…': 'Se verifică autentificarea unică…',
  'Single sign-on did not complete.': 'Autentificarea unică nu s-a finalizat.',
  'Powered by': 'Susținut de',

  /* Lista de cereri */
  'New request': 'Cerere nouă',
  'All statuses': 'Toate stările',
  Status: 'Stare',
  'No requests': 'Nicio cerere',
  'You have no requests with this status.':
    'Nu ai nicio cerere cu această stare.',
  'You have not submitted any request yet.':
    'Nu ai trimis nicio cerere încă.',
  'Loading your requests…': 'Se încarcă cererile…',

  /* Formularul „Cerere" - tichetul direct */
  'New ticket': 'Cerere',
  'Open a new ticket': 'Deschide un tichet nou',
  'For anything that is not in the catalog: describe it and it goes straight to the service desk.':
    'Pentru orice nu e în catalog: descrie și ajunge direct la service desk.',
  'Ticket type': 'Tip de tichet',
  'Choose a type': 'Alege tipul',
  Subject: 'Subiect',
  'A few words about what you need.': 'Câteva cuvinte despre ce ai nevoie.',
  Description: 'Descriere',
  'What happened, what you need, since when.':
    'Ce s-a întâmplat, ce ai nevoie, de când.',
  'Goes on the ticket, for whoever picks it up.':
    'Ajunge pe tichet, pentru cine îl preia.',
  'Open ticket': 'Deschide tichetul',
  'Opening…': 'Se deschide…',
  'The ticket could not be opened.': 'Tichetul nu a putut fi deschis.',
  /* Confirmarea de dupa trimitere. Nu poarta numarul: se pleaca imediat pe fisa
     tichetului, unde numarul scrie mare in titlu, iar un numar spus de doua ori
     in aceeasi clipa nu adauga nimic. */
  'Ticket opened.': 'Tichetul a fost deschis.',
  'Choose a ticket type.': 'Alege tipul de tichet.',
  'Write a short subject.': 'Scrie un subiect scurt.',
  'Describe what you need.': 'Descrie ce ai nevoie.',
  'Loading ticket types…': 'Se încarcă tipurile de tichet…',
  'No ticket types available': 'Niciun tip de tichet disponibil',
  'Direct tickets are not configured yet. Contact the service desk.':
    'Tichetele directe nu sunt configurate încă. Contactează service desk-ul.',

  /* Caseta care rezolvă singură */
  /* Intrebarea din caseta si titlul ecranului. Doua formulari, dinadins: una
     e adresata omului, cealalta spune despre ce e ecranul. */
  'How can we help you today?': 'Cum te putem ajuta astăzi?',
  'What do you need?': 'De ce ai nevoie?',
  'Your request': 'Cererea ta',
  'Printer prints nothing, my password expired, I need a laptop…':
    'Nu merge imprimanta, mi-a expirat parola, am nevoie de un laptop…',
  'We search all the help articles and the catalog automatically. If nothing matches, we open a support request.':
    'Căutăm automat în toate articolele de ajutor și în catalog. Dacă nu se potrivește nimic, deschidem o cerere de suport.',
  'Searching…': 'Se caută…',
  /*
   * Două grupe, două etichete. Catalogul răspunde la „vreau să cer ceva",
   * articolele la „ceva nu merge" — puse laolaltă, omul nu mai știe ce e o
   * comandă și ce e o instrucțiune.
   */
  'Solve it yourself': 'Rezolvă singur',
  'Request from the catalog': 'Cere din catalog',
  'Read the steps': 'Vezi pașii',
  Help: 'Ajutor',
  'Log an IT Support request': 'Înregistrează o cerere de Suport IT',
  /* Fara "din stanga" / "din dreapta": pe ecran ingust coloanele cad una sub
     alta si nu mai exista laturi, iar textul ar fi aratat spre un loc care nu
     mai e acolo. "De alaturi" e adevarat in amandoua asezarile. */
  'Nothing matched what you wrote. The form next to this goes to the service desk.':
    'Nu s-a potrivit nimic cu ce ai scris. Formularul de alături ajunge la service desk.',
  'Nothing we have matches, so this goes to the service desk.':
    'Nimic din ce avem nu se potrivește, deci ajunge la service desk.',
  'I would rather open a support request': 'Prefer să deschid direct o cerere de suport',

  /* Fisa unui tichet deschis direct */
  'Loading ticket…': 'Se încarcă tichetul…',
  'Assigned to': 'Se ocupă',

  /* Cine a cerut, arătat chiar lui: e primul loc în care poate verifica pe ce
     date îl va căuta cineva din IT. */
  'Requested by': 'Cerut de',
  'Contact': 'Contact',
  'Lately': 'În ultima vreme',
  'Tickets you opened in the last 7 days:': 'Tichete deschise de tine în ultimele 7 zile:',

  /*
   * „Conversaţie", nu „Comentarii".
   *
   * De când omul poate şi scrie, nu doar citi, cuvântul trebuie să spună asta:
   * „Comentarii" e ce lasă cineva sub un articol şi nu aşteaptă răspuns.
   */
  Conversation: 'Conversație',
  /* Titlul coloanei din dreapta de pe fisa unui tichet. Nu „Conversație":
     acolo omul nu vine sa poarte o discutie, ci sa revina cu ceva ce i s-a
     cerut sau ce a uitat sa spuna. */
  'Follow-up': 'Revenire',
  /* Numai pentru cititoarele de ecran, dupa numarul din clopotel: o cifra
     singura n-ar fi spus a ce. Intrarea de meniu cu acelasi nume a plecat -
     clopotelul sta acum pe randul „Cererile mele". */
  'new replies': 'raspunsuri noi',

  /* Fereastra in care se vede un fisier de pe tichet */
  'Preview': 'Vezi',
  'Close': 'Închide',
  /* „Loading…" e deja mai sus, in dictionar - se refoloseste. */
  'Open in a new tab': 'Deschide în filă nouă',
  /* Pe telefon nu exista file, iar fisierul pleaca intr-o alta aplicatie -
     deci si butonul spune altceva. Vezi FilePreview. */
  'Open the file': 'Deschide fișierul',
  'The file could not be loaded. Reload the page and try again.':
    'Fișierul nu a putut fi încărcat. Reîncarcă pagina și încearcă din nou.',
  'This browser cannot show the file here.':
    'Browserul nu poate afișa fișierul aici. Deschide-l în filă nouă.',
  /* Pe telefon: PDF-urile nu se pot arata in aplicatie, dar se deschid in afara
     ei. Se spune ce URMEAZA sa se intample, nu ce nu se poate. */
  'This file opens in another app.': 'Fișierul se deschide într-o altă aplicație.',
  'No app on this device can open this file.':
    'Nicio aplicație de pe telefon nu poate deschide acest fișier.',
  'The service desk has replied': 'Service desk-ul ti-a raspuns',

  /* Fisiere */
  'Attach files': 'Atașează fișiere',
  Files: 'Fișiere',
  'Images or PDF, up to 10 MB each, at most 5.':
    'Imagini sau PDF, cel mult 10 MB fiecare, maximum 5.',
  'The files could not be sent.': 'Fișierele nu au putut fi trimise.',
  You: 'Tu',
  'Nobody has written on this ticket yet.': 'Nu ți-a scris nimeni încă pe acest tichet.',
  'Write to the service desk': 'Scrie-le celor de la service desk',
  'Add something, or answer what you were asked.':
    'Adaugă ceva, sau răspunde la ce ți s-a cerut.',
  'Goes to whoever has the ticket. They can answer here.':
    'Ajunge la cine are tichetul. Îți poate răspunde tot aici.',
  'Send': 'Trimite',
  'Sending…': 'Se trimite…',
  'Your reply has been sent.': 'Răspunsul tău a plecat.',
  'The reply could not be sent.': 'Răspunsul nu a putut fi trimis.',
  'Everything you have sent, newest first — from the catalog or straight to the service desk.':
    'Tot ce ai trimis, cele mai noi primele — din catalog sau direct la service desk.',
  /* Nu „a eșuat", nu chenar roșu: formularul de tichet e chiar dedesubt și
     merge. Omul nu e blocat, doar nu i-am putut oferi scurtătura. */
  'The search is not answering right now.': 'Căutarea nu răspunde acum.',

  /* Baza de cunoștințe, răsfoită */
  'Help articles': 'Articole de ajutor',
  'Things you can do yourself, without waiting for anybody.':
    'Lucruri pe care le poți face singur, fără să aștepți pe nimeni.',
  'Printer, password, VPN…': 'Imprimantă, parolă, VPN…',
  'Loading articles…': 'Se încarcă articolele…',
  'No articles': 'Niciun articol',
  'Nothing has been written for your company yet.':
    'Nu s-a scris încă nimic pentru compania ta.',
  'See all help articles': 'Vezi toate articolele de ajutor',
  /* Când articolele au răspuns, „niciun serviciu" nu mai e un eșec: omul are
     deja ceva de citit deasupra, iar textul obișnuit l-ar trimite să caute
     altceva — adică exact departe de răspunsul pe care tocmai i l-am dat. */
  'Nothing in the catalog matches, but the articles above might help.':
    'Nimic din catalog nu se potrivește, dar articolele de mai sus te-ar putea ajuta.',
  /* Unitatile din subsolul listelor. Vin ca text englezesc de la ecran si trec
     prin `t()` in Pager. */
  article: 'articol',
  articles: 'articole',
  service: 'serviciu',
  services: 'servicii',
  request: 'cerere',
  requests: 'cereri',

  /* Articolul */
  'Loading the article…': 'Se încarcă articolul…',
  'Still stuck? Open a support request and it goes to the service desk.':
    'Tot nu merge? Deschide o cerere de suport și ajunge la service desk.',
  'Open a support request': 'Deschide o cerere de suport',
  'You came from the article': 'Ai venit de la articolul',
  'The article did not solve it, so this goes to the service desk.':
    'Articolul nu a rezolvat problema, așa că cererea ajunge la service desk.',

  /* Stari */
  'This screen could not be loaded': 'Acest ecran nu a putut fi încărcat',
  'Error code': 'Cod eroare',

  /*
   * Mesajele care vin din stratul de api.
   *
   * Astea se vad tocmai cand omul e deja blocat - reteaua a cazut, serviciul
   * nu raspunde, sesiunea s-a inchis. Un ecran pe jumatate romanesc si pe
   * jumatate englezesc e cel mai prost loc in care poti sa lasi pe cineva
   * care oricum nu intelege ce se intampla.
   */
  'Cannot reach the service. Check your connection and try again.':
    'Nu se poate ajunge la serviciu. Verifică conexiunea și încearcă din nou.',
  'The service did not answer in time. Please try again.':
    'Serviciul nu a răspuns la timp. Încearcă din nou.',
  'The service answered with something that is not JSON. It may be misconfigured.':
    'Serviciul a răspuns cu altceva decât se aștepta. Anunță service desk-ul.',
  'Your session has ended. Please sign in again.':
    'Sesiunea s-a încheiat. Autentifică-te din nou.',
  'You do not have permission to do this.': 'Nu ai dreptul să faci asta.',
  'This item does not exist, or it is not available to your company.':
    'Elementul nu există sau nu este disponibil pentru compania ta.',
  'This conflicts with the current state of the request.':
    'Asta intră în conflict cu starea de acum a cererii.',
  'The service is currently unavailable. Please try again later.':
    'Serviciul nu este disponibil momentan. Încearcă mai târziu.',
  'The request could not be completed.': 'Cererea nu a putut fi dusă la capăt.',
  'Something went wrong.': 'Ceva nu a mers bine.',
  Pagination: 'Paginare',
  Uncategorised: 'Fără categorie',
  'No description provided.': 'Nu a fost scrisă nicio descriere.',
  'Could not add to cart.': 'Nu s-a putut adăuga în coș.',
  'Could not change the quantity.': 'Nu s-a putut schimba cantitatea.',
  'One more': 'Încă unul',
  /* Titlul unei cereri cu mai multe articole: „Card issuing și încă 2". */
  'and {n} more': 'și încă {n}',
  'One less': 'Unul mai puțin',
  'Priority': 'Prioritate',
  'Leave it to the service desk': 'Lasă la latitudinea service desk-ului',
  'Last update': 'Ultima modificare',
  'nobody yet': 'încă nimeni',

  /* Propozitiile lungi */
  'Submitting creates one request and a ticket for each item. Anything that needs approval is approved on its ticket, not in the shop.':
    'La trimitere se creează o cerere și câte un tichet pentru fiecare articol. Ce are nevoie de aprobare se aprobă pe tichetul lui, nu în magazin.',
  'Access is granted by your company administrator. Contact the service desk if you cannot sign in.':
    'Accesul este acordat de administratorul companiei tale. Contactează service desk-ul dacă nu te poți autentifica.',
  "Everything you have submitted, newest first. Approval happens on each item's ticket — open a request to see where it stands.":
    'Tot ce ai trimis, cele mai noi primele. Aprobarea se face pe tichetul fiecărui articol — deschide o cerere ca să vezi unde a ajuns.',
  'Usage-based parts are billed on the actual monthly counts.':
    'Componentele pe consum se facturează după cifrele reale ale lunii.',
  'Privacy policy': 'Politica de confidențialitate',
};

const DICTIONARIES: Record<Lang, Record<string, string>> = {
  en: {},
  ro: RO,
};

/**
 * Limba de la pornire.
 *
 * Ce a ales omul data trecuta bate limba browserului: alegerea explicita e un
 * raspuns, preferinta browserului e o ghicitura. Fara niciuna, romana - e un
 * catalog pentru o banca romaneasca, si englezescul e alegerea celui care il
 * cere anume.
 */
function initial(): Lang {
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);

    if (saved === 'en' || saved === 'ro') return saved;
  } catch {
    /* Stocarea poate fi oprita din browser. Nu e motiv sa cada aplicatia. */
  }

  return 'ro';
}

let current: Lang = initial();

/* Si de la pornire, nu doar la schimbare: fara asta, prima pagina ramane cu
   `lang` din index.html si e citita cu accent gresit de cititoarele de ecran. */
document.documentElement.lang = current;

export function getLang(): Lang {
  return current;
}

export function setLang(lang: Lang): void {
  current = lang;

  try {
    window.localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    /* Vezi mai sus: fara stocare, limba tine cat tine fila. */
  }

  /* Si pe document, pentru cititoarele de ecran si pentru despartirea in
     silabe. Un `lang="en"` pe o pagina romaneasca e citit cu accent englezesc. */
  document.documentElement.lang = lang;
}

/**
 * Textul in limba curenta.
 *
 * Fara traducere, se intoarce chiar textul englezesc primit. Nu se scrie in
 * consola si nu se marcheaza pe ecran: lipsa unei traduceri nu e o eroare de
 * program, e o propozitie care n-a apucat sa fie tradusa.
 */
export function t(text: string): string {
  return DICTIONARIES[current][text] ?? text;
}
